/**
 * HelpIcon Component
 * Displays an info icon that triggers documentation modal when clicked
 */

import React, { useState, useEffect } from 'react';
import { FiInfo, FiHelpCircle } from 'react-icons/fi';
import documentationService from '../../services/DocumentationService';
import DocumentationModal from './DocumentationModal';

const BADGE_SIZE = 17;

const HelpIcon = ({
    documentationId,
    size = 16,
    className = '',
    variant = 'info', // 'info' or 'help'
    position = 'top-right', // position relative to parent
    appearance = 'default', // 'default' | 'badge'
    badgeCorner = 'top-left', // 'top-left' | 'top-right' — only for badge appearance
}) => {
    const [showModal, setShowModal] = useState(false);
    const [hasDocumentation, setHasDocumentation] = useState(false);
    const [isServiceInitialized, setIsServiceInitialized] = useState(false);

    useEffect(() => {
        // Initialize documentation service if not already done
        const initializeService = async () => {
            if (!documentationService.initialized) {
                await documentationService.initialize();
            }
            setIsServiceInitialized(true);
            setHasDocumentation(documentationService.hasDocumentation(documentationId));
        };

        initializeService();
    }, [documentationId]);

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (hasDocumentation) {
            setShowModal(true);
            documentationService.trackUsage(documentationId, 'quickHelp');
        } else {
            console.warn(`No documentation found for ID: ${documentationId}`);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    // Don't render if service isn't initialized or no documentation exists
    if (!isServiceInitialized || !hasDocumentation) {
        return null;
    }

    const Icon = variant === 'help' ? FiHelpCircle : FiInfo;
    const isBadge = appearance === 'badge';
    const iconSize = isBadge ? 9 : size;
    const buttonSize = isBadge ? BADGE_SIZE : size + 8;

    const baseClasses = isBadge
        ? `
            inline-flex items-center justify-center
            absolute z-20 p-0
            rounded-full
            bg-white/95 backdrop-blur-[2px]
            border border-slate-200/80
            text-slate-400 hover:text-slate-600
            hover:border-slate-300 hover:bg-white
            shadow-[0_1px_2px_rgba(15,23,42,0.05)]
            opacity-75 hover:opacity-100
            transition-all duration-150
            cursor-pointer
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-1
            ${className}
        `.trim()
        : `
            inline-flex items-center justify-center
            text-gray-500 hover:text-blue-600
            transition-colors duration-200
            cursor-pointer
            rounded-full
            hover:bg-blue-50
            ${className}
        `.trim();

    const positionClasses = {
        'top-right': 'absolute top-1 right-1',
        'top-left': 'absolute top-1 left-1',
        'bottom-right': 'absolute bottom-1 right-1',
        'bottom-left': 'absolute bottom-1 left-1',
        'inline': '',
        'relative': 'relative',
    };

    const badgeCornerClasses = {
        'top-left': 'top-0 left-0 -translate-x-[30%] -translate-y-[30%]',
        'top-right': 'top-0 right-0 translate-x-[30%] -translate-y-[30%]',
    };

    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                className={`${baseClasses} ${isBadge ? badgeCornerClasses[badgeCorner] : positionClasses[position]}`}
                style={{
                    width: buttonSize,
                    height: buttonSize,
                    minWidth: buttonSize,
                    minHeight: buttonSize,
                }}
                title="Get help about this feature"
                aria-label={`Documentation for ${documentationId}`}
            >
                <Icon size={iconSize} strokeWidth={isBadge ? 2.25 : undefined} />
            </button>

            {showModal && (
                <DocumentationModal
                    documentationId={documentationId}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
};

export default HelpIcon; 